import React from "react";
import RouterLink from "next/link";
import clsx from "clsx";

import { cn, textVariants, Link, LinkVariant } from "@jasperlabs/ui";

import RenderIfCondition from "@/components/organisms/RenderIfCondition";
import {
	BehindFeature,
	useFeatureFlag,
} from "@/components/utilities/featureFlag";
import NotificationsCountPill from "@/components/layout/AppHeader/NotificationsLink/NotificationsCountPill";

const LINK_STYLES = cn(
	textVariants({ variant: "subheading-default" }),
	"font-semibold flex w-full items-center text-black",
);

const ITEM_STYLES = "px-6 py-4";

const NavItemWithCountPill = ({
	name,
	count,
}: {
	name: string;
	count: number;
}) => (
	<div className="flex items-center gap-1">
		{name}
		<RenderIfCondition condition={count > 0}>
			<div className="flex items-center justify-center h-4 min-w-[16px] px-[6px] bg-primary-400 text-white rounded-full text-xs leading-none">
				{count}
			</div>
		</RenderIfCondition>
	</div>
);

type MainNavViewProps = {
	onClose: () => void;
	hasOffers: boolean;
	offersCount: number;
	sellOrdersCount: number;
};

/**
 * Primary ("main") navigation links shown when the burger toggle is active.
 * Uses the cross-entity labels (Holding Movements / Alerts). Account links
 * live in AccountNavView.
 */
const MainNavView = ({
	onClose,
	hasOffers,
	offersCount,
	sellOrdersCount,
}: MainNavViewProps) => {
	const hasSellOrders = sellOrdersCount > 0;
	const isDocumentsHidden = useFeatureFlag("HIDE_DOCUMENTS");

	return (
		<ul className="flex w-full flex-col divide-y divide-neutral-200">
			<RenderIfCondition condition={!!hasOffers}>
				<li className={ITEM_STYLES}>
					<Link
						as={RouterLink}
						href="/invest"
						variant={LinkVariant.Black}
						onClick={onClose}
						className={LINK_STYLES}
					>
						<NavItemWithCountPill name="Invest" count={offersCount} />
					</Link>
				</li>
			</RenderIfCondition>
			<RenderIfCondition condition={!!hasSellOrders}>
				<li className={ITEM_STYLES}>
					<Link
						as={RouterLink}
						href="/trade"
						variant={LinkVariant.Black}
						onClick={onClose}
						className={LINK_STYLES}
					>
						<NavItemWithCountPill name="Trade" count={sellOrdersCount} />
					</Link>
				</li>
			</RenderIfCondition>
			<li className={ITEM_STYLES}>
				<Link
					as={RouterLink}
					href="/portfolio"
					variant={LinkVariant.Black}
					onClick={onClose}
					className={LINK_STYLES}
				>
					Portfolio
				</Link>
			</li>
			<li className={ITEM_STYLES}>
				<Link
					as={RouterLink}
					href="/orders"
					variant={LinkVariant.Black}
					onClick={onClose}
					className={LINK_STYLES}
				>
					Orders
				</Link>
			</li>
			{!isDocumentsHidden && (
				<li className={ITEM_STYLES}>
					<Link
						as={RouterLink}
						href="/documents"
						variant={LinkVariant.Black}
						onClick={onClose}
						className={LINK_STYLES}
					>
						Documents
					</Link>
				</li>
			)}
			<li className={ITEM_STYLES}>
				<Link
					as={RouterLink}
					href="/holding-movements"
					variant={LinkVariant.Black}
					onClick={onClose}
					className={LINK_STYLES}
				>
					Holding Movements
				</Link>
			</li>
			<BehindFeature feature="DISTRIBUTION_TRANSACTIONS_TABLE">
				<li className={ITEM_STYLES}>
					<Link
						as={RouterLink}
						href="/distributions"
						variant={LinkVariant.Black}
						onClick={onClose}
						className={LINK_STYLES}
					>
						Distributions
					</Link>
				</li>
			</BehindFeature>
			<li className={ITEM_STYLES}>
				<Link
					as={RouterLink}
					variant={LinkVariant.Black}
					href="/notifications"
					onClick={onClose}
					className={clsx("gap-x-2", LINK_STYLES)}
				>
					Alerts
					<NotificationsCountPill size="sm" />
				</Link>
			</li>
		</ul>
	);
};

export default MainNavView;
